/**
 * Gifts & ledger → **Withdrawal & sweep** (GH-885; owner, 2026-10-04), the page that used to be
 * "Ledger & grants".
 *
 *   GET /v1/organizations/:organizationId/accounting/received-by-event      reconciliation.view
 *
 * What was received from donors toward the organization and toward each event, from the ledger.
 * Every figure is the server's; the console sums nothing. A pledge is never in here.
 */
import { z } from "zod";
import { signedMoneySchema } from "./v1.js";
export const receivedByEventRowSchema = z.object({
    /** Null for postings that named no event; they read as the organization itself. */
    eventId: z.string().uuid().nullable(),
    eventName: z.string().nullable(),
    /** The organization's general fund — gifts to the organization rather than to one event. */
    isGeneralFund: z.boolean(),
    eventStartsAt: z.string().nullable(),
    /** Credited from donors' gifts, after refunds, reversals and lost disputes. */
    credited: signedMoneySchema,
    /** Cash and cheques the team recorded. Already in the organization's hands; never sent. */
    recordedElsewhere: signedMoneySchema,
    giftCount: z.number().int().nonnegative(),
});
export const receivedByEventResponseSchema = z.object({
    rows: z.array(receivedByEventRowSchema),
    /** The server's own totals of the rows above (the console never adds them up). */
    totals: z.object({
        credited: signedMoneySchema,
        recordedElsewhere: signedMoneySchema,
    }),
});
/* ------------------------------------------------- the destination account (GH-885) */
/**
 *   POST /v1/organizations/:organizationId/accounting/bank-link/start      organization.manage_bank
 *   POST /v1/organizations/:organizationId/accounting/bank-link/complete   organization.manage_bank
 *   GET  /v1/admin/bank-account-reviews                                     platform staff
 *   POST /v1/admin/bank-account-reviews/:bankAccountId/decision             platform staff
 *
 * The browser gets a Link token and gives back Link's public token and the account the person chose.
 * The exchange, the access token, the owner names and the IRS check all stay on the server; the
 * access token is never on any wire.
 */
export const organizationBankLinkStartResponseSchema = z.object({
    linkToken: z.string(),
    environment: z.enum(["sandbox", "development", "production"]),
});
export const organizationBankLinkCompleteRequestSchema = z.object({
    publicToken: z.string().min(1).max(500),
    /** Link's account id for the account chosen; checked against the item's own list. */
    accountId: z.string().min(1).max(200),
});
export const organizationBankLinkCompleteResponseSchema = z.object({
    verificationStatus: z.enum(["verified_automatically", "pending_review"]),
    verificationReasons: z.array(z.string()),
    last4: z.string().length(4),
    bankName: z.string().nullable(),
    payableFrom: z.string().nullable(),
});
export const bankAccountReviewRowSchema = z.object({
    bankAccountId: z.string().uuid(),
    organizationId: z.string().uuid(),
    organizationName: z.string(),
    organizationLegalName: z.string(),
    ein: z.string().nullable(),
    bankName: z.string().nullable(),
    last4: z.string().length(4),
    /** As typed, or the owner name the bank reported. */
    accountHolderName: z.string(),
    /** Every owner name the bank reported (Plaid Identity); null for a typed account. */
    accountOwnerNames: z.array(z.string()).nullable(),
    /** The IRS legal name read for the EIN when the account was added; null if it was not read. */
    irsLegalName: z.string().nullable(),
    method: z.enum(["plaid_identity", "manual_entry"]),
    reasons: z.array(z.string()),
    addedAt: z.string(),
    payableFrom: z.string().nullable(),
});
export const bankAccountReviewListResponseSchema = z.object({
    accounts: z.array(bankAccountReviewRowSchema),
});
export const bankAccountReviewDecisionSchema = z.object({
    decision: z.enum(["verified_by_staff", "rejected"]),
    /** How the account was checked (a call, a statement, a voided cheque) or why it was refused. */
    note: z.string().trim().min(3).max(1000),
});
/**
 *   POST /v1/organizations/:organizationId/accounting/payout-connect/onboard   organization.manage_bank
 *
 * The Stripe Connect attach-once setup (GH-908, DEC-032): creates the organization's Express
 * account if it does not have one, attaches its already-Plaid-verified bank, and hands back a
 * fresh Stripe-hosted onboarding link for whatever KYC Stripe still needs. The two URLs are where
 * the admin console sends the browser back after Stripe's hosted flow finishes or is abandoned.
 */
export const organizationPayoutConnectOnboardRequestSchema = z.object({
    refreshUrl: z.string().url(),
    returnUrl: z.string().url(),
});
export const organizationPayoutConnectOnboardResponseSchema = z.object({
    onboardingUrl: z.string().url(),
});
//# sourceMappingURL=withdrawal.js.map